/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-non-null-asserted-optional-chain */
/* eslint-disable no-param-reassign */
import { Col, DatePicker, Input, Button, Spin, InputNumber } from 'antd'
import { useTranslation } from 'react-i18next'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import dayjs from 'dayjs'
import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQueryClient } from 'react-query'
import { ErrorMessage } from '@hookform/error-message'

import { TitleAndDivider } from '@components'
import { QUERY_KEYS } from '@api'
import { StatusTicketEnum, Ticket } from '@types'
import { ArrayUtils, StringUtils } from '@utils'
import { useGetTickets, usePurchases } from '@hooks'

import { FormType, validationSchema, validationUpdateSchema } from './validations'
import {
  Wrapper,
  UploadButton,
  Container,
  StyledRow,
  StyledSelect,
  StyledText,
  UploadSingleFile,
  StyledErrorMessage,
  StyledRequired,
} from './components'
import { useCreateTruckDocumentation, useDestinations, useUpdateTruckDocumentation } from './hooks'
import { useSocket } from '../../../hooks/useSocket'

interface Props {
  location?: { state?: { ticket: Ticket } }
}

const AddTruckDocumentation: React.FC<Props> = ({ location }) => {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const { createTruckDocumentation, isLoading } = useCreateTruckDocumentation()
  const { updateTruckDocumentation } = useUpdateTruckDocumentation()
  const { destinationsOptions, isLoading: isDestinationsLoading } = useDestinations()
  const { tickets, ticketsIsLoading } = useGetTickets({
    limit: 30000,
    statusName: [StatusTicketEnum.CONFIRMED],
  })
  const queryClient = useQueryClient()

  // Conexión WebSocket y actualizaciones de tickets
  const { connect, socket } = useSocket()

  // Conectar a WebSocket cuando el componente se monta
  useEffect(() => {
    connect()
  }, [connect])

  // Escuchar actualizaciones de tickets directamente en el socket
  useEffect(() => {
    if (!socket) return

    const handleTicketUpdate = () => {
      queryClient.invalidateQueries([QUERY_KEYS.TICKETS])
      queryClient.invalidateQueries([QUERY_KEYS.TICKET])
      queryClient.invalidateQueries([QUERY_KEYS.TO_AUTHORIZE])
    }

    socket.on('ticket-update', handleTicketUpdate)

    return () => {
      socket.off('ticket-update', handleTicketUpdate)
    }
  }, [socket, queryClient])

  const navigate = useNavigate()
  const {
    materialOptions,
    materialsLoading,
    getPurchasesByMaterial,
    documentNumberIdsLoading,
    documentIdsOptions,
  } = usePurchases()

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<FormType>({
    resolver: yupResolver(location?.state ? validationUpdateSchema : validationSchema),
  })

  useEffect(() => {
    if (!location?.state?.ticket) {
      reset({
        invoiceDate: undefined,
        invoiceNumber: undefined,
        destinationId: undefined,
        invoiceAmount: undefined,
        materialId: undefined,
        materialDescription: undefined,
        purchaseOrderId: undefined,
      })
    }

    window.addEventListener('popstate', () => {
      if (pathname === '/dashboard/suppliers/truck-documentation') navigate('/dashboard/suppliers')
    })
    // eslint-disable-next-line no-restricted-globals
  }, [history, location?.state, reset])

  const handleForm = (form: FormType) => {
    createTruckDocumentation(form)
  }
  const handleUpdateForm: SubmitHandler<FormType> = form => {
    updateTruckDocumentation({
      ...form,
      photos: form.photos ?? [],
      truckDocumentationId: location?.state?.ticket.truckDocumentation?.id!,
    })
  }

  const isDisabled = (ticket: Ticket | undefined) => {
    if (ticket) {
      return ticket.status.name === StatusTicketEnum.DELIVERED
    }
    return false
  }

  const confirmedTickets = tickets?.tickets
    .reduce(
      (
        result: {
          value: number
          label: string
          withdrawalDate: Date
        }[],
        ticket,
      ) => {
        if (ticket.status.name === StatusTicketEnum.CONFIRMED)
          result = [
            ...result,
            {
              value: ticket.id,
              label: `${ticket.id} - ${ticket.carrier.businessName} - ${dayjs(
                ticket.withdrawalDate,
              ).format('ll hh:mm a')}`,
              withdrawalDate: ticket.withdrawalDate,
            },
          ]

        return result
      },
      [],
    )
    .sort((a, b) => new Date(a.withdrawalDate).getTime() - new Date(b.withdrawalDate).getTime())

  const handleMaterialChange = (value: any) => {
    setValue('purchaseOrderId', '')
    const material = materialOptions!.find(option => option.value === value)
    setValue('materialDescription', material!.label)
    getPurchasesByMaterial({ materialId: value as string })
  }

  useEffect(() => {
    if (location?.state?.ticket.truckDocumentation?.materialId)
      getPurchasesByMaterial({
        materialId: location?.state?.ticket.truckDocumentation?.materialId,
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Wrapper>
      <Spin spinning={isLoading || ticketsIsLoading}>
        <div>
          <TitleAndDivider
            title={t('ta_invoice.add_truck_documentation.title_and_divider.new_truck.title')}
          />
          <StyledRow>
            <Col sm={{ span: 16 }} md={{ span: 8 }}>
              <StyledText>
                {t('ta_invoice.add_truck_documentation.title_and_divider.new_truck.carrier')}:
                <StyledRequired> *</StyledRequired>
              </StyledText>
            </Col>
            <Col sm={{ span: 8 }} md={{ span: 16 }}>
              <Controller
                control={control}
                name="ticketId"
                render={({ field }) => (
                  <StyledSelect
                    placeholder={t('globals.placeholder_select')}
                    options={confirmedTickets}
                    {...field}
                    disabled={!!location?.state}
                    value={location?.state && location?.state.ticket.carrier.businessName}
                  />
                )}
              />
              <Col>
                <StyledErrorMessage>
                  <ErrorMessage errors={errors} name="ticketId" />
                </StyledErrorMessage>
              </Col>
            </Col>
          </StyledRow>
          <StyledRow>
            <Col sm={{ span: 16 }} md={{ span: 8 }}>
              <StyledText>
                {t('ta_invoice.add_truck_documentation.title_and_divider.new_truck.depot')}:
                <StyledRequired> *</StyledRequired>
              </StyledText>
            </Col>
            <Col sm={{ span: 8 }} md={{ span: 16 }}>
              <Controller
                control={control}
                name="destinationId"
                defaultValue={location?.state?.ticket.destination.id}
                render={({ field }) => (
                  <StyledSelect
                    placeholder={t('globals.placeholder_select')}
                    options={destinationsOptions}
                    loading={isDestinationsLoading}
                    disabled={!!location?.state}
                    {...field}
                    value={field.value}
                  />
                )}
              />
              <Col>
                <StyledErrorMessage>
                  <ErrorMessage errors={errors} name="destinationId" />
                </StyledErrorMessage>
              </Col>
            </Col>
          </StyledRow>
        </div>
        <Container>
          <TitleAndDivider
            title={t(
              'ta_invoice.add_truck_documentation.title_and_divider.supplier_documentation.title',
            )}
          />
          <StyledRow>
            <Col span={8}>
              <StyledText>
                {t(
                  'ta_invoice.add_truck_documentation.title_and_divider.supplier_documentation.invoice_date',
                )}
                :<StyledRequired> *</StyledRequired>
              </StyledText>
            </Col>
            <Col span={16}>
              <Controller
                name="invoiceDate"
                control={control}
                defaultValue={dayjs(
                  typeof location?.state?.ticket.truckDocumentation?.invoiceDate === 'string'
                    ? (location?.state?.ticket.truckDocumentation?.invoiceDate as string)
                    : (location?.state?.ticket.truckDocumentation?.invoiceDate[0] as string),
                ).toDate()}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    allowClear={false}
                    value={field.value ? dayjs(field.value).utc() : undefined}
                    disabled={isDisabled(location?.state ? location?.state.ticket : undefined)}
                    picker="date"
                    format="DD/MM/YYYY"
                  />
                )}
              />
              <Col>
                <StyledErrorMessage>
                  <ErrorMessage errors={errors} name="invoiceDate" />
                </StyledErrorMessage>
              </Col>
            </Col>
          </StyledRow>
          <StyledRow>
            <Col span={8}>
              <StyledText>
                {t(
                  'ta_invoice.add_truck_documentation.title_and_divider.supplier_documentation.invoice_number',
                )}
                :<StyledRequired> *</StyledRequired>
              </StyledText>
            </Col>
            <Col span={16}>
              <Controller
                control={control}
                name="invoiceNumber"
                rules={{ required: true }}
                defaultValue={
                  location?.state?.ticket.truckDocumentation?.invoiceNumber &&
                  location?.state.ticket.truckDocumentation?.invoiceNumber
                }
                render={({ field }) => (
                  <Input
                    {...field}
                    value={field.value ?? undefined}
                    disabled={isDisabled(location?.state ? location?.state.ticket : undefined)}
                  />
                )}
              />
              <Col>
                <StyledErrorMessage>
                  <ErrorMessage errors={errors} name="invoiceNumber" />
                </StyledErrorMessage>
              </Col>
            </Col>
          </StyledRow>
          <StyledRow>
            <Col span={8}>
              <StyledText>
                {t(
                  'ta_invoice.add_truck_documentation.title_and_divider.supplier_documentation.invoice_amount',
                )}
                :<StyledRequired> *</StyledRequired>
              </StyledText>
            </Col>
            <Col span={16}>
              <Controller
                control={control}
                name="invoiceAmount"
                defaultValue={
                  location?.state?.ticket.truckDocumentation?.invoiceAmount &&
                  location?.state.ticket.truckDocumentation?.invoiceAmount
                }
                render={({ field }) => (
                  <InputNumber
                    style={{ width: '150px' }}
                    {...field}
                    value={field.value ?? undefined}
                    disabled={isDisabled(location?.state ? location?.state.ticket : undefined)}
                    step="0.01"
                    precision={2}
                    formatter={value => {
                      if (!value) return '$ '
                      const decimals =
                        value?.toString().search(/\./) >= 0
                          ? value?.toString().split('.').reverse()[0].slice(0, 2)
                          : null
                      const number = `$ ${Math.floor(value)}`.replace(/\B(?=(\d{3})+(?!.))/g, ',')
                      if (decimals) return `${number}.${decimals}`
                      return `${number}`
                    }}
                  />
                )}
              />
              <Col>
                <StyledErrorMessage>
                  <ErrorMessage errors={errors} name="invoiceAmount" />
                </StyledErrorMessage>
              </Col>
            </Col>
          </StyledRow>
          <StyledRow>
            <Col span={8}>
              <StyledText>
                {t(
                  'ta_invoice.add_truck_documentation.title_and_divider.supplier_documentation.material',
                )}
                :<StyledRequired> *</StyledRequired>
              </StyledText>
            </Col>
            <Col sm={{ span: 8 }} md={{ span: 16 }}>
              <Controller
                control={control}
                name="materialId"
                defaultValue={
                  location?.state?.ticket.truckDocumentation?.materialId &&
                  location?.state.ticket.truckDocumentation?.materialId.toString()
                }
                render={({ field }) => (
                  <>
                    <StyledSelect
                      placeholder={t('globals.placeholder_select_material')}
                      options={materialOptions}
                      loading={materialsLoading}
                      onSelect={handleMaterialChange}
                      disabled={isDisabled(location?.state ? location?.state.ticket : undefined)}
                      {...field}
                      value={field.value}
                    />
                    <Col>
                      <StyledErrorMessage>
                        <ErrorMessage errors={errors} name="materialId" />
                      </StyledErrorMessage>
                    </Col>
                  </>
                )}
              />
              <Controller
                control={control}
                name="materialDescription"
                defaultValue={
                  location?.state?.ticket.truckDocumentation?.materialDescription &&
                  location?.state.ticket.truckDocumentation?.materialDescription.toString()
                }
                render={({ field }) => <Input {...field} value={field.value ?? undefined} hidden />}
              />
            </Col>
          </StyledRow>
          <StyledRow>
            <Col span={8}>
              <StyledText>
                {t(
                  'ta_invoice.add_truck_documentation.title_and_divider.supplier_documentation.PO',
                )}
                :
              </StyledText>
            </Col>
            <Col sm={{ span: 8 }} md={{ span: 16 }}>
              <Controller
                control={control}
                name="purchaseOrderId"
                defaultValue={
                  location?.state?.ticket.truckDocumentation?.purchaseOrderId &&
                  location?.state.ticket.truckDocumentation?.purchaseOrderId
                }
                render={({ field }) => (
                  <StyledSelect
                    placeholder={t('globals.placeholder_select_po')}
                    options={documentIdsOptions}
                    loading={documentNumberIdsLoading}
                    disabled={isDisabled(location?.state ? location?.state.ticket : undefined)}
                    {...field}
                    value={field.value}
                  />
                )}
              />
            </Col>
          </StyledRow>
          <StyledRow>
            <Col span={8}>
              <StyledText>
                {t(
                  'ta_invoice.add_truck_documentation.title_and_divider.supplier_documentation.invoice_sell',
                )}
                :<StyledRequired> *</StyledRequired>
              </StyledText>
            </Col>
            <Col span={16}>
              <Controller
                control={control}
                name="salesInvoice"
                render={({ field }) => (
                  <UploadSingleFile
                    {...field}
                    fileData={ArrayUtils.reverse(
                      location?.state?.ticket.truckDocumentation?.salesInvoice,
                    )?.map(salesInvoice => ({
                      key: salesInvoice,
                      name: StringUtils.formatFilename(
                        location?.state?.ticket.id ?? 0,
                        salesInvoice,
                        'InvoiceSell',
                        'pdf',
                      ),
                    }))}
                  />
                )}
              />
              <Col>
                <StyledErrorMessage>
                  <ErrorMessage errors={errors} name="salesInvoice" />
                </StyledErrorMessage>
              </Col>
            </Col>
          </StyledRow>
          <StyledRow>
            <Col span={8}>
              <StyledText>
                {t(
                  'ta_invoice.add_truck_documentation.title_and_divider.supplier_documentation.weight_certificate',
                )}
                :<StyledRequired> *</StyledRequired>
              </StyledText>
            </Col>
            <Col span={16}>
              <Controller
                control={control}
                name="weightCertificate"
                render={({ field }) => (
                  <UploadSingleFile
                    {...field}
                    fileData={ArrayUtils.reverse(
                      location?.state?.ticket.truckDocumentation?.weightCertificate,
                    )?.map(weightCertificate => ({
                      key: weightCertificate,
                      name: StringUtils.formatFilename(
                        location?.state?.ticket.id ?? 0,
                        weightCertificate,
                        'WeightCertificate',
                        'pdf',
                      ),
                    }))}
                  />
                )}
              />
              <Col>
                <StyledErrorMessage>
                  <ErrorMessage errors={errors} name="weightCertificate" />
                </StyledErrorMessage>
              </Col>
            </Col>
          </StyledRow>
          <StyledRow>
            <Col span={8}>
              <StyledText>
                {' '}
                B/L:<StyledRequired> *</StyledRequired>
              </StyledText>
            </Col>
            <Col span={16}>
              <Controller
                control={control}
                name="BL"
                render={({ field }) => (
                  <UploadSingleFile
                    {...field}
                    fileData={ArrayUtils.reverse(
                      location?.state?.ticket.truckDocumentation?.BL,
                    )?.map(document => ({
                      key: document,
                      name: StringUtils.formatFilename(
                        location?.state?.ticket.id ?? 0,
                        document,
                        'BL',
                        'pdf',
                      ),
                    }))}
                  />
                )}
              />
              <Col>
                <StyledErrorMessage>
                  <ErrorMessage errors={errors} name="BL" />
                </StyledErrorMessage>
              </Col>
            </Col>
          </StyledRow>
          <StyledRow>
            <Col span={8}>
              <StyledText>
                No Radioact. Cert.:<StyledRequired> *</StyledRequired>
              </StyledText>
            </Col>
            <Col span={16}>
              <Controller
                control={control}
                name="radioactiveCertificate"
                render={({ field }) => (
                  <UploadSingleFile
                    {...field}
                    fileData={ArrayUtils.reverse(
                      location?.state?.ticket.truckDocumentation?.radioactiveCertificate,
                    )?.map(radioactiveCertificate => ({
                      key: radioactiveCertificate,
                      name: StringUtils.formatFilename(
                        location?.state?.ticket.id ?? 0,
                        radioactiveCertificate,
                        'RadioactiveCertificate',
                        'pdf',
                      ),
                    }))}
                  />
                )}
              />
              <Col>
                <StyledErrorMessage>
                  <ErrorMessage errors={errors} name="radioactiveCertificate" />
                </StyledErrorMessage>
              </Col>
            </Col>
          </StyledRow>
          <StyledRow>
            <Col span={8}>
              <StyledText>
                {t(
                  'ta_invoice.add_truck_documentation.title_and_divider.supplier_documentation.photos',
                )}
                :<StyledRequired> *</StyledRequired>
              </StyledText>
            </Col>
            <Col span={16}>
              <Controller
                control={control}
                name="photos"
                render={({ field }) => (
                  <UploadButton
                    accept="image/*"
                    {...field}
                    fileData={ArrayUtils.reverse(
                      location?.state?.ticket.truckDocumentation?.photos,
                    )?.map(photo => ({
                      key: `${photo}`,
                      name: StringUtils.formatFilename(
                        location?.state?.ticket.id ?? 0,
                        photo,
                        'photo',
                        'png',
                      ),
                    }))}
                  />
                )}
              />
              <Col>
                <StyledErrorMessage>
                  <ErrorMessage errors={errors} name="photos" />
                </StyledErrorMessage>
              </Col>
            </Col>
          </StyledRow>
          <StyledRow>
            <Col span={8}>
              <StyledText>
                (<StyledRequired>* </StyledRequired>required)
              </StyledText>
            </Col>
          </StyledRow>
          <Button
            onClick={location?.state ? handleSubmit(handleUpdateForm) : handleSubmit(handleForm)}
            disabled={isDisabled(location?.state ? location?.state.ticket : undefined)}
          >
            {location?.state
              ? t('ta_invoice.add_truck_documentation.buttons.update')
              : t('ta_invoice.add_truck_documentation.buttons.upload')}
          </Button>
        </Container>
      </Spin>
    </Wrapper>
  )
}

export { AddTruckDocumentation }
